import { default as React } from 'react';
import { Content, Header, Page } from '@backstage/core-components';
import {
  CatalogTable,
  CatalogTableColumnsFunc,
  CatalogTableRow,
} from '@backstage/plugin-catalog';
import {
  EntityListProvider,
  useEntityList,
  EntityKindPicker,
} from '@backstage/plugin-catalog-react';
import {
  useAnalytics,
  configApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { makeStyles } from '@mui/styles';
import {
  Button,
  Box,
  Typography,
  CircularProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@material-ui/core';
import { Alert } from '@mui/lab';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VpnLockIcon from '@mui/icons-material/VpnLock';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { useNavigate } from 'react-router-dom';
import {
  BarChart,
  Bar,
  ResponsiveContainer,
  YAxis,
  XAxis,
  Tooltip,
} from 'recharts';

import { formatDuration } from './utils';
import { MicroappsOnlyFilter } from './MicroappTypeFilter';
import { MicroappCardGrid } from './MicroappCardGrid';
import { UnregisterAppButton } from './UnregisterAppButton';
import {
  useCurrentUserEmail,
  isUserAuthorizedForEntity,
  useSkyStageAdminEmails,
} from './hooks';
import { CatalogViewToggle, ViewMode } from '../CatalogViewToggle';

// ── Types ────────────────────────────────────────────────────────────────────

type DayStatus = 'operational' | 'degraded' | 'outage';
type IncidentLayer = 'infrastructure' | 'application' | 'both';

interface DailyStatusEntry {
  date: string;
  infra_status: DayStatus;
  app_status: DayStatus;
}

interface FleetAppSummary {
  app_name: string;
  owner: string;
  infra_uptime_pct: number;
  app_uptime_pct: number;
  overall_uptime_pct: number;
  current_status: string;
  last_incident_date: string | null;
  last_incident_duration_minutes: number | null;
  last_incident_layer: IncidentLayer | null;
  recent_14days: DailyStatusEntry[];
}

interface FleetDailyBreakdown {
  date: string;
  avg_infra_pct: number;
  avg_app_pct: number;
}

interface FleetSummaryResponse {
  period_days: number;
  fleet_infra_uptime_pct: number;
  fleet_infra_incident_count: number;
  fleet_infra_total_downtime_minutes: number;
  avg_app_uptime_pct: number;
  fleet_app_incident_count: number;
  fleet_app_total_downtime_minutes: number;
  total_microapps: number;
  apps_below_threshold: number;
  fleet_daily_breakdown: FleetDailyBreakdown[];
  apps: FleetAppSummary[];
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatIncidentDate(
  dateStr: string | null,
  durationMinutes: number | null,
): string {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  const month = d.toLocaleString('default', { month: 'short' });
  const day = d.getDate();
  const dur = durationMinutes ? ` (${formatDuration(durationMinutes)})` : '';
  return `${month} ${day}${dur}`;
}

function getUptimeColor(pct: number): string {
  if (pct >= 99.9) return '#2e7d32';
  if (pct >= 99) return '#e65100';
  return '#c62828';
}

function getDayColor(day: DailyStatusEntry): string {
  if (day.infra_status === 'outage' || day.app_status === 'outage') {
    return '#c62828';
  }
  if (day.infra_status === 'degraded' || day.app_status === 'degraded') {
    return '#e65100';
  }
  return '#4caf50';
}

const LAYER_CHIP: Record<IncidentLayer, { bg: string; label: string }> = {
  application: { bg: '#7b1fa2', label: 'App' },
  infrastructure: { bg: '#1565c0', label: 'Infra' },
  both: { bg: '#00695c', label: 'Both' },
};

// ── Styles ────────────────────────────────────────────────────────────────────

type MicroappPageProps = {
  columns: CatalogTableColumnsFunc;
};

const useStyles = makeStyles(theme => ({
  tableWrapper: {
    '& .MuiPaper-root': {
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
      borderRadius: theme.spacing(1),
      overflow: 'hidden',
    },
    '& .MuiTableHead-root': {
      '& .MuiTableCell-head': {
        backgroundColor: theme.palette.mode === 'dark' ? '#1f1f1f' : '#f5f7fa',
        fontWeight: 600,
        fontSize: '0.875rem',
        color: theme.palette.mode === 'dark' ? '#fff' : '#333',
        borderBottom: `2px solid ${theme.palette.divider}`,
        padding: '16px',
      },
    },
    '& .MuiTableBody-root': {
      '& .MuiTableRow-root': {
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          backgroundColor:
            theme.palette.mode === 'dark'
              ? 'rgba(255, 255, 255, 0.05)'
              : 'rgba(0, 0, 0, 0.02)',
          transform: 'translateY(-1px)',
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
        },
        '& .MuiTableCell-body': {
          padding: '16px',
          fontSize: '0.875rem',
          borderBottom: `1px solid ${theme.palette.divider}`,
        },
      },
    },
    '& .MuiIconButton-root': {
      transition: 'all 0.2s ease-in-out',
      '&:hover': {
        backgroundColor: `${theme.palette.primary.main}15`,
        transform: 'scale(1.1)',
      },
    },
  },
  alertBox: {
    flex: 1,
    marginRight: theme.spacing(2),
    backgroundColor: 'transparent',
    border: `1px solid ${theme.palette.info.main}30`,
    borderRadius: theme.spacing(1),
    minWidth: 0,
    '& .MuiAlert-icon': {
      color: theme.palette.info.main,
      flexShrink: 0,
    },
    '& .MuiAlert-message': {
      display: 'flex',
      alignItems: 'center',
      flexWrap: 'nowrap',
      fontSize: '0.875rem',
      lineHeight: 1.5,
      whiteSpace: 'nowrap',
    },
    [theme.breakpoints.down('md')]: {
      marginRight: 0,
      marginBottom: theme.spacing(2),
      '& .MuiAlert-message': {
        whiteSpace: 'normal',
        flexWrap: 'wrap',
      },
    },
  },
  vpnBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '3px',
    verticalAlign: 'middle',
    margin: '0 4px',
    color: '#228189',
    whiteSpace: 'nowrap',
    flexShrink: 0,
    padding: '3px 6px',
    backgroundColor: 'rgba(34, 129, 137, 0.1)',
    borderRadius: '4px',
    fontSize: '0.65rem',
    fontWeight: 700,
    letterSpacing: '0.3px',
    textTransform: 'uppercase' as const,
    lineHeight: 1,
  },
  vpnIcon: {
    fontSize: '12px',
    color: '#228189',
    flexShrink: 0,
  },
  buttonGroup: {
    display: 'flex',
    gap: theme.spacing(1.5),
    flexShrink: 0,
    [theme.breakpoints.down('md')]: {
      width: '100%',
      justifyContent: 'flex-end',
    },
  },
  headerContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'nowrap',
    gap: theme.spacing(2),
    '@media (max-width: 1400px)': {
      transform: 'scale(0.95)',
      transformOrigin: 'left center',
    },
    '@media (max-width: 1200px)': {
      transform: 'scale(0.9)',
      transformOrigin: 'left center',
    },
    '@media (max-width: 1024px)': {
      transform: 'scale(0.85)',
      transformOrigin: 'left center',
    },
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
      alignItems: 'stretch',
      transform: 'none',
    },
  },
  registerButton: {
    textTransform: 'none',
    fontWeight: 600,
    minWidth: '150px',
    borderRadius: theme.spacing(1),
    borderWidth: '2px',
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
      borderWidth: '2px',
      transform: 'translateY(-2px)',
      boxShadow: '0 4px 8px rgba(0, 0, 0, 0.15)',
    },
  },
  deployButton: {
    textTransform: 'none',
    fontWeight: 600,
    minWidth: '150px',
    borderRadius: theme.spacing(1),
    transition: 'all 0.2s ease-in-out',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
    },
  },
}));

const usePanelStyles = makeStyles(_theme => ({
  panel: {
    border: '1px solid #e0e0e0',
    borderRadius: 8,
    marginBottom: 24,
    overflow: 'hidden',
    background: '#fff',
  },
  pill: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '10px 16px',
    cursor: 'pointer',
    userSelect: 'none' as const,
    background: '#f9fafb',
    borderBottom: '1px solid #e0e0e0',
    '&:hover': { background: '#f1f3f5' },
  },
  cardsRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: 0,
    borderBottom: '1px solid #e0e0e0',
  },
  card: {
    padding: '16px 20px',
    borderRight: '1px solid #e0e0e0',
    '&:last-child': { borderRight: 'none' },
  },
  cardValue: {
    fontSize: '1.75rem',
    fontWeight: 700,
    lineHeight: 1.1,
    color: '#1a1a1a',
  },
  cardLabel: {
    fontSize: '0.65rem',
    fontWeight: 700,
    color: '#777',
    marginTop: 2,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.06em',
  },
  cardSubtitle: {
    fontSize: '0.72rem',
    color: '#888',
    marginTop: 2,
  },
  chartWrap: {
    marginTop: 8,
    height: 48,
  },
  tableSection: {
    padding: '16px 20px',
  },
  tableTitleRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 10,
  },
  tableHead: {
    '& .MuiTableCell-head': {
      fontSize: '0.7rem',
      fontWeight: 700,
      color: '#888',
      textTransform: 'uppercase' as const,
      letterSpacing: '0.4px',
      padding: '6px 10px',
      borderBottom: '1px solid #e0e0e0',
      background: '#f9fafb',
    },
  },
  tableBody: {
    '& .MuiTableCell-body': {
      fontSize: '0.8rem',
      padding: '8px 10px',
      borderBottom: '1px solid #f0f0f0',
    },
    '& .MuiTableRow-root:last-child .MuiTableCell-body': {
      borderBottom: 'none',
    },
  },
}));

// ── PlatformUptimePanel ───────────────────────────────────────────────────────

const PlatformUptimePanel = () => {
  const classes = usePanelStyles();
  const config = useApi(configApiRef);
  const fetchApi = useApi(fetchApiRef);

  const [expanded, setExpanded] = React.useState(false);
  const [fleet, setFleet] = React.useState<FleetSummaryResponse | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(false);

  // Fetch on mount so the % is visible in the collapsed pill immediately
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const backendUrl = config.getString('backend.baseUrl');
        const res = await fetchApi.fetch(
          `${backendUrl}/api/catalog-api/availability/fleet/summary?days=30`,
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data: FleetSummaryResponse = await res.json();
        if (!cancelled) {
          setFleet(data);
          setError(false);
        }
      } catch {
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [config, fetchApi]);

  const infraPct = fleet ? fleet.fleet_infra_uptime_pct.toFixed(2) : null;
  const appPct = fleet ? fleet.avg_app_uptime_pct.toFixed(2) : null;
  const belowCount = fleet ? fleet.apps_below_threshold : null;
  const totalApps = fleet ? fleet.total_microapps : null;

  const infraBreakdown = (fleet?.fleet_daily_breakdown ?? []).map(d => ({
    date: d.date,
    pct: d.avg_infra_pct,
  }));
  const appBreakdown = (fleet?.fleet_daily_breakdown ?? []).map(d => ({
    date: d.date,
    pct: d.avg_app_pct,
  }));

  return (
    <Box className={classes.panel}>
      {/* Collapsed pill — always shows live % */}
      <Box
        className={classes.pill}
        role="button"
        aria-expanded={expanded}
        tabIndex={0}
        onClick={() => setExpanded(e => !e)}
      >
        <FiberManualRecordIcon style={{ fontSize: 10, color: '#2e7d32' }} />
        <Typography style={{ fontWeight: 600, fontSize: '0.875rem', flex: 1 }}>
          Platform Uptime:&nbsp;
          {loading && (
            <CircularProgress size={11} style={{ verticalAlign: 'middle' }} />
          )}
          {!loading && !error && infraPct && `${infraPct}%`}
          {!loading && (error || !infraPct) && 'Unavailable'}
        </Typography>
        <Typography
          style={{ fontSize: '0.75rem', color: '#888', marginRight: 4 }}
        >
          30 days
        </Typography>
        {expanded ? (
          <ExpandLessIcon fontSize="small" style={{ color: '#2e7d32' }} />
        ) : (
          <ExpandMoreIcon fontSize="small" style={{ color: '#2e7d32' }} />
        )}
      </Box>

      {/* Expanded body */}
      {expanded && (
        <>
          {error && (
            <Box p={2}>
              <Typography variant="body2" color="textSecondary">
                No fleet availability data yet.
              </Typography>
            </Box>
          )}

          {!error && (
            <>
              {/* 3 metric cards (Gap 2: "Apps tracked" removed) */}
              <Box className={classes.cardsRow}>
                {/* Card 1: Infrastructure Uptime */}
                <Box className={classes.card}>
                  <Box
                    style={{ display: 'flex', alignItems: 'baseline', gap: 1 }}
                  >
                    <Typography className={classes.cardValue}>
                      {loading ? '—' : infraPct}
                    </Typography>
                    {!loading && (
                      <Typography
                        style={{
                          fontSize: '1rem',
                          fontWeight: 700,
                          color: '#1a1a1a',
                          lineHeight: 1.1,
                        }}
                      >
                        %
                      </Typography>
                    )}
                  </Box>
                  <Typography className={classes.cardLabel}>
                    Avg infra uptime
                  </Typography>
                  {/* Gap 3: correct subtitle */}
                  <Typography className={classes.cardSubtitle}>
                    {loading
                      ? '…'
                      : `Last 30 days · ${
                          fleet?.fleet_infra_incident_count ?? 0
                        } incident(s) (${formatDuration(
                          fleet?.fleet_infra_total_downtime_minutes ?? null,
                        )} total)`}
                  </Typography>
                  <Box className={classes.chartWrap}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={infraBreakdown} barCategoryGap="15%">
                        <XAxis dataKey="date" hide />
                        <YAxis domain={[98, 100]} hide />
                        <Tooltip
                          formatter={v => [
                            typeof v === 'number' ? `${v.toFixed(2)}%` : '—',
                            'Infra',
                          ]}
                          labelFormatter={l => `Date: ${l}`}
                        />
                        <Bar
                          dataKey="pct"
                          fill="#1976d2"
                          radius={[2, 2, 0, 0]}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                </Box>

                {/* Card 2: Average App Uptime */}
                <Box className={classes.card}>
                  <Box
                    style={{ display: 'flex', alignItems: 'baseline', gap: 1 }}
                  >
                    <Typography className={classes.cardValue}>
                      {loading ? '—' : appPct}
                    </Typography>
                    {!loading && (
                      <Typography
                        style={{
                          fontSize: '1rem',
                          fontWeight: 700,
                          color: '#1a1a1a',
                          lineHeight: 1.1,
                        }}
                      >
                        %
                      </Typography>
                    )}
                  </Box>
                  <Typography className={classes.cardLabel}>
                    Avg app uptime
                  </Typography>
                  {/* Gap 3: correct subtitle */}
                  <Typography className={classes.cardSubtitle}>
                    {loading
                      ? '…'
                      : `Last 30 days · Across ${totalApps} hosted microapp(s)`}
                  </Typography>
                  <Box className={classes.chartWrap}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={appBreakdown} barCategoryGap="15%">
                        <XAxis dataKey="date" hide />
                        <YAxis domain={[98, 100]} hide />
                        <Tooltip
                          formatter={v => [
                            typeof v === 'number' ? `${v.toFixed(2)}%` : '—',
                            'App',
                          ]}
                          labelFormatter={l => `Date: ${l}`}
                        />
                        <Bar
                          dataKey="pct"
                          fill="#7b1fa2"
                          radius={[2, 2, 0, 0]}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                </Box>

                {/* Card 3: Apps Below 99% */}
                <Box className={classes.card}>
                  <Typography
                    className={classes.cardValue}
                    style={{
                      color:
                        belowCount !== null && belowCount > 0
                          ? '#c62828'
                          : '#2e7d32',
                    }}
                  >
                    {loading ? '—' : belowCount}
                  </Typography>
                  <Typography className={classes.cardLabel}>
                    Apps below 99%
                  </Typography>
                  {/* Gap 3: correct subtitle */}
                  <Typography className={classes.cardSubtitle}>
                    {loading ? '…' : `of ${totalApps} hosted microapps`}
                  </Typography>
                  {!loading && belowCount !== null && belowCount > 0 && (
                    <Typography
                      className={classes.cardSubtitle}
                      style={{ cursor: 'pointer', marginTop: 4 }}
                      onClick={() => {
                        document
                          .getElementById('uptime-by-microapp-table')
                          ?.scrollIntoView({ behavior: 'smooth' });
                      }}
                    >
                      See table below for details
                    </Typography>
                  )}
                </Box>
              </Box>

              {/* Gap 4: Per-app table */}
              <Box
                className={classes.tableSection}
                id="uptime-by-microapp-table"
              >
                <Box className={classes.tableTitleRow}>
                  <Typography style={{ fontWeight: 600, fontSize: '0.875rem' }}>
                    Uptime by Microapp (Last 30 Days)
                  </Typography>
                  {totalApps !== null && (
                    <Typography style={{ fontSize: '0.75rem', color: '#888' }}>
                      {totalApps} apps tracked ·{' '}
                      {fleet?.apps_below_threshold ?? 0} below 99%
                    </Typography>
                  )}
                </Box>

                {loading && (
                  <Box display="flex" justifyContent="center" py={2}>
                    <CircularProgress size={24} />
                  </Box>
                )}

                {!loading && fleet && (
                  <Table size="small">
                    <TableHead className={classes.tableHead}>
                      <TableRow>
                        <TableCell>App</TableCell>
                        <TableCell>Owner</TableCell>
                        <TableCell>Overall Uptime</TableCell>
                        <TableCell>Last 14 Days</TableCell>
                        <TableCell>Last Incident</TableCell>
                        <TableCell>Incident Layer</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody className={classes.tableBody}>
                      {fleet.apps.map(app => {
                        const color = getUptimeColor(app.overall_uptime_pct);
                        const layerStyle = app.last_incident_layer
                          ? LAYER_CHIP[app.last_incident_layer]
                          : null;
                        return (
                          <TableRow key={app.app_name}>
                            <TableCell>
                              <Typography
                                variant="body2"
                                style={{
                                  color: '#1976d2',
                                  cursor: 'pointer',
                                  fontWeight: 500,
                                }}
                                component="a"
                                href={`/catalog/default/component/${app.app_name}`}
                              >
                                {app.app_name}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color="textSecondary">
                                {app.owner || '—'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Box
                                display="flex"
                                alignItems="center"
                                style={{ gap: 6 }}
                              >
                                <FiberManualRecordIcon
                                  style={{ fontSize: 10, color }}
                                />
                                <Typography
                                  variant="body2"
                                  style={{ color, fontWeight: 600 }}
                                >
                                  {app.overall_uptime_pct.toFixed(2)}%
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <span
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'flex-end',
                                  gap: 1,
                                  height: 20,
                                }}
                              >
                                {app.recent_14days.map(day => (
                                  <span
                                    key={day.date}
                                    title={`${day.date}: infra=${day.infra_status} app=${day.app_status}`}
                                    style={{
                                      display: 'inline-block',
                                      width: 4,
                                      height: '100%',
                                      backgroundColor: getDayColor(day),
                                      borderRadius: 1,
                                    }}
                                  />
                                ))}
                              </span>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color="textSecondary">
                                {formatIncidentDate(
                                  app.last_incident_date,
                                  app.last_incident_duration_minutes,
                                )}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              {layerStyle ? (
                                <Chip
                                  label={layerStyle.label}
                                  size="small"
                                  style={{
                                    backgroundColor: layerStyle.bg,
                                    color: '#fff',
                                    fontSize: '0.68rem',
                                    height: 20,
                                    fontWeight: 600,
                                  }}
                                />
                              ) : (
                                <Typography
                                  variant="body2"
                                  color="textSecondary"
                                >
                                  —
                                </Typography>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </Box>
            </>
          )}
        </>
      )}
    </Box>
  );
};

// ── MicroappTableWithCount ────────────────────────────────────────────────────

const MicroappTableWithCount = ({
  columns,
  viewMode,
}: {
  columns: CatalogTableColumnsFunc;
  viewMode: ViewMode;
}) => {
  const { entities, updateFilters } = useEntityList();
  const config = useApi(configApiRef);
  const count = entities.length;
  const analytics = useAnalytics();
  const navigate = useNavigate();
  const classes = useStyles();
  const currentUserEmail = useCurrentUserEmail();
  const adminEmails = useSkyStageAdminEmails();

  React.useEffect(() => {
    analytics.captureEvent('sign_in', 'skystage-login', {
      attributes: {
        platform: config.getString('app.title'),
        page_name: 'microapps',
        page_title: 'Microapps Catalog',
        microapps_count: count,
      },
    });
  }, [analytics, count, config]);

  const handleDeleteSuccess = React.useCallback(() => {
    updateFilters({ _refresh: Date.now() } as any);
  }, [updateFilters]);

  const customActions = React.useMemo(
    () => [
      (rowData: any) => {
        const entity = rowData.entity || rowData;
        const namespace = entity.metadata?.namespace || 'default';
        const kind = entity.kind?.toLowerCase() || 'component';
        const name = entity.metadata?.name;

        return {
          icon: () => <VisibilityIcon fontSize="small" />,
          tooltip: 'View Details',
          onClick: () => {
            navigate(`/catalog/${namespace}/${kind}/${name}`);
          },
        };
      },
    ],
    [navigate],
  );

  const columnsWithDelete: CatalogTableColumnsFunc = React.useCallback(
    entityListContext => {
      const baseColumns = columns(entityListContext);

      const deleteColumn = {
        title: 'Actions',
        field: 'actions-delete',
        width: '80px',
        sorting: false,
        render: (rowData: CatalogTableRow) => {
          const entity = (rowData as any).entity || rowData;
          if (!entity?.metadata) return null;

          const isAuthorized = isUserAuthorizedForEntity(
            entity,
            currentUserEmail,
            adminEmails,
          );
          if (!isAuthorized) return null;

          return (
            <UnregisterAppButton
              entity={entity}
              iconOnly
              onDeleted={handleDeleteSuccess}
            />
          );
        },
      };

      return [...baseColumns, deleteColumn];
    },
    [columns, currentUserEmail, adminEmails, handleDeleteSuccess],
  );

  return (
    <>
      <div style={{ display: 'none' }}>
        <EntityKindPicker initialFilter="component" />
        <MicroappsOnlyFilter />
      </div>
      {viewMode === 'cards' ? (
        <MicroappCardGrid />
      ) : (
        <div className={classes.tableWrapper}>
          <CatalogTable
            columns={columnsWithDelete}
            title={`All Apps (${count})`}
            actions={customActions}
          />
        </div>
      )}
    </>
  );
};

// ── MicroappPage ──────────────────────────────────────────────────────────────

export const MicroappPage = ({ columns }: MicroappPageProps) => {
  const navigate = useNavigate();
  const classes = useStyles();
  const [viewMode, setViewMode] = React.useState<ViewMode>('cards');

  const handleRegisterApp = (templateName: string) => {
    navigate(`/create/templates/default/${templateName}`);
  };

  return (
    <Page themeId="home">
      <Header
        title="Apps"
        subtitle="Catalog of Registered Apps and Hosted Microapps"
      />
      <Content>
        <Box mb={3} className={classes.headerContainer}>
          <Alert severity="info" className={classes.alertBox}>
            Apps marked with{' '}
            <span className={classes.vpnBadge}>
              <VpnLockIcon className={classes.vpnIcon} />
              VPN
            </span>{' '}
            require VPN connection to access.
          </Alert>
          <Box className={classes.buttonGroup}>
            <Button
              variant="outlined"
              color="primary"
              size="medium"
              startIcon={<AddIcon />}
              onClick={() => handleRegisterApp('register-internal-apps')}
              className={classes.registerButton}
            >
              Register App
            </Button>
            <Button
              variant="contained"
              color="primary"
              size="medium"
              startIcon={<AddIcon />}
              onClick={() => handleRegisterApp('microapp-hosting')}
              className={classes.deployButton}
            >
              Deploy App
            </Button>
            <CatalogViewToggle viewMode={viewMode} onChange={setViewMode} />
          </Box>
        </Box>

        <PlatformUptimePanel />

        <EntityListProvider>
          <MicroappTableWithCount columns={columns} viewMode={viewMode} />
        </EntityListProvider>
      </Content>
    </Page>
  );
};
